import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  ShortPayRecommendationResponseSchema,
  DismissibleArCollectionActionSchema,
} from "./ar-short-pay-recommendations.contract";

/**
 * AR Over-Payment Recommendation RPC Contract
 *
 * Twin of the short-pay recommendation contract: an AI agent reviews the
 * evidence assembled on an `over_payment` collection thread and produces a
 * ranked list of recommended actions (refund, apply credit, issue credit memo,
 * loop in the rep) drawn from the shared ArCollectionAction taxonomy, each
 * grounded in the specific evidence behind it.
 *
 * The request/response shapes are identical to short-pay's, so the schemas are
 * reused directly rather than re-declared. Auto-generated when an over-payment
 * thread is created; regenerable on demand. Results are cached per-thread in the
 * generic content-addressed intelligence cache with a data fingerprint for
 * staleness.
 */
export const arOverPaymentRecommendationsContracts = {
  /** Retrieve the cached recommendation for a thread (no AI call). */
  get: oc
    .input(z.object({ threadId: z.string().uuid() }))
    .output(ShortPayRecommendationResponseSchema)
    .route({
      method: "GET",
      summary: "Get over-payment recommendations for a collection thread",
      description:
        "Returns the cached AI-ranked actions (refund, apply credit, issue credit memo, contact the rep) for a over-payment collection thread, with evidence, staleness flag, salesperson and muted actions. No AI call.",
    }),
  /** Trigger a fresh AI analysis for a thread and cache it. */
  regenerate: oc
    .input(z.object({ threadId: z.string().uuid() }))
    .output(ShortPayRecommendationResponseSchema)
    .route({
      method: "POST",
      summary: "Regenerate over-payment recommendations",
      description:
        "Runs a fresh AI analysis of a over-payment collection thread's evidence, caches the ranked recommended actions, and returns them.",
    }),
  /**
   * Mute a recommended action for this thread's customer ("don't suggest this
   * again"). Writes a customer-scoped memory bank note and returns the cached
   * recommendation with the action already filtered out. Org-wide: every thread
   * for that customer stops surfacing the action.
   */
  dismissAction: oc
    .input(
      z.object({
        threadId: z.string().uuid(),
        action: DismissibleArCollectionActionSchema,
        /** Optional free-text reason, stored on the note for later review. */
        reason: z.string().trim().max(500).optional(),
      }),
    )
    .output(ShortPayRecommendationResponseSchema)
    .route({
      method: "POST",
      summary: "Dismiss a recommended over-payment action",
      description:
        "Stops suggesting an action for the thread's customer on every thread by saving a customer memory note with optional reason; returns the filtered recommendations.",
    }),
} as const;
